// Shared types matching backend/src/types/index.ts

export interface Die {
  value: number; // 1-6
}

export interface Player {
  id: string;
  name: string;
  dice: Die[];
  isConnected: boolean;
  isAI?: boolean;
  emoji?: string;
  aiFallback?: boolean; // True if AI used fallback strategy on last turn
  ready?: boolean; // True if player is ready (in waiting phase or revealing phase)
  lastThoughts?: string; // AI reasoning from most recent turn (only sent during reveal)
}

export interface Bid {
  quantity: number;
  faceValue: number; // 1-6
  playerId: string;
}

export interface GameState {
  id: string;
  players: Player[];
  currentPlayerIndex: number;
  currentBid: Bid | null;
  bidHistory: Bid[]; // History of bids in current round
  round: number;
  roundStartPlayerIndex: number; // Index of player who opens each round
  phase: 'waiting' | 'playing' | 'revealing' | 'round_end' | 'game_over';
  winner: string | null;
  lastAction: string;
  dicePerPlayer: number; // Number of dice each player starts with
  playersReady?: string[]; // Player IDs who are ready for next round (during revealing phase)
  lastChallengeResult?: ChallengeResult; // Most recent challenge result (during revealing phase)
}

export enum MessageType {
  // Client -> Server
  JOIN_GAME = 'join_game',
  ADD_AI_PLAYER = 'add_ai_player',
  REMOVE_AI_PLAYER = 'remove_ai_player',
  SET_DICE_COUNT = 'set_dice_count',
  START_GAME = 'start_game',
  MAKE_BID = 'make_bid',
  CHALLENGE = 'challenge',
  READY_UP = 'ready_up',
  LEAVE_GAME = 'leave_game',

  // Server -> Client
  GAME_STATE = 'game_state',
  PLAYER_JOINED = 'player_joined',
  PLAYER_LEFT = 'player_left',
  GAME_STARTED = 'game_started',
  BID_MADE = 'bid_made',
  CHALLENGE_RESULT = 'challenge_result',
  ROUND_END = 'round_end',
  GAME_OVER = 'game_over',
  ERROR = 'error',
  YOUR_DICE = 'your_dice'
}

export interface Message {
  type: MessageType;
  payload?: any;
  error?: string;
}

export interface JoinGamePayload {
  playerName: string;
  gameId?: string;
  playerId?: string; // For reconnection
}

export interface MakeBidPayload {
  quantity: number;
  faceValue: number;
}

export interface SetDiceCountPayload {
  dicePerPlayer: number;
}

export interface ChallengeResult {
  challenger: string;
  challenged: string;
  bid: Bid;
  actualCount: number;
  loser: string;
  revealedDice: { [playerId: string]: Die[] };
  aiThoughts: { [playerId: string]: string }; // AI reasoning for each AI player
}
